import React, { useEffect } from 'react';
import Button from './Button';

// Always shows first, last and current +/- 1; gaps become '...'
const getPageItems = (page, totalPages) => {
   const pages = new Set([1, totalPages, page - 1, page, page + 1]);
   const sorted = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);

   const items = [];
   sorted.forEach((p, i) => {
      if (i > 0) {
         const prev = sorted[i - 1];
         if (p - prev === 2) items.push(prev + 1);
         else if (p - prev > 2) items.push('...');
      }
      items.push(p);
   });
   return items;
};

const Pagination = ({ page, totalPages, total, limit, onPageChange }) => {
   useEffect(() => {
      const handleKeyDown = (e) => {
         const activeTag = document.activeElement?.tagName?.toLowerCase();
         if (['input', 'textarea', 'select'].includes(activeTag)) return;

         if (e.key === 'ArrowLeft' && page > 1) {
            onPageChange(page - 1);
         } else if (e.key === 'ArrowRight' && page < totalPages) {
            onPageChange(page + 1);
         }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
   }, [page, totalPages, onPageChange]);

   if (totalPages <= 1) return null;

   const from = total === 0 ? 0 : (page - 1) * limit + 1;
   const to = Math.min(page * limit, total);

   return (
      <div className="flex items-center justify-between">
         <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing {from}–{to} of {total} results
         </p>
         <div className="flex items-center gap-3">
            <Button
               size="sm"
               variant="secondary"
               disabled={page <= 1}
               onClick={() => onPageChange(page - 1)}
            >
               Previous
            </Button>
            <div className="flex items-center gap-1">
               {getPageItems(page, totalPages).map((item, i) =>
                  item === '...' ? (
                     <span key={`gap-${i}`} className="px-1 text-sm text-gray-600 dark:text-gray-400">
                        …
                     </span>
                  ) : (
                     <Button
                        key={item}
                        size="sm"
                        variant={item === page ? 'primary' : 'outline'}
                        aria-current={item === page ? 'page' : undefined}
                        onClick={() => onPageChange(item)}
                     >
                        {item}
                     </Button>
                  )
               )}
            </div>
            <Button
               size="sm"
               variant="secondary"
               disabled={page >= totalPages}
               onClick={() => onPageChange(page + 1)}
            >
               Next
            </Button>
         </div>
      </div>
   );
};

export default Pagination;
